// Primary Components
import EmployeeAccountPanel from '../../components/People/EmployeeAccountPanel/EmployeeAccountPanel';
import UserAccountPanel from '../../components/People/UserAccountPanel/UserAccountPanel';

// User Account Panel Components
import EditPersonalDetails from '../../components/People/UserAccountPanel/components/EditPersonalDetails/EditPersonalDetails';
import UserNotificationSettings from '../../components/People/UserAccountPanel/components/UserNotificationSettings/UserNotificationSettings';

// Employee Account Panel Components
import EmployeePersonalDetailsView from '../../components/People/EmployeeAccountPanel/components/EmployeePersonalDetailsView/EmployeePersonalDetailsView';
import EmployeeShiftView from '../../components/People/EmployeeAccountPanel/components/EmployeeShiftView';
import UserAvailabilityPanel from '../../components/People/UserAccountPanel/components/UserAvailabilityPanel/UserAvailabilityPanel';
import EditEvent from '../../components/People/UserAccountPanel/components/UserAvailabilityPanel/components/EditEvent/EditEvent';
import EmployeeAvailabilityView from '../../components/People/EmployeeAccountPanel/components/EmployeeAvailabilityView/EmployeeAvailabilityView';

// Tips Components
import RecordPaymentDrawer from '../../components/Tips/RecordPaymentDrawer';
import TipsHistoryDrawer from '../../components/Tips/TipsHistoryDrawer';

/**
 * Drawer Types Enum
 * Hierarchy:
 * - Base Drawers (no parent)
 *   - Employee Account Panel
 *   - User Account Panel
 *   - Record Payment
 *   - Tips History
 *
 * - User Account Children
 *   - Edit Personal Details
 *   - Availability Panel
 *   - Edit Notifications
 *
 * - Availability Panel Children (User Account Grandchildren)
 *   - Edit Availability
 *   - Create Availability
 *
 * - Employee Account Children
 *   - View Personal Details
 *   - View Shifts
 *   - View Availability
 */
export const DRAWER_TYPES = {
  // Base Drawer Types
  EMPLOYEE: 'employee',
  USER: 'user',
  RECORD_PAYMENT: 'recordPayment',
  TIPS_HISTORY: 'tipsHistory',

  // User Account Panel Children
  EDIT_PERSONAL_DETAILS: 'editPersonalDetails',
  AVAILABILITY: 'availability',
  EDIT_NOTIFICATIONS: 'editNotifications',

  // Availability Children
  EDIT_AVAILABILITY: 'editAvailability',
  CREATE_AVAILABILITY: 'createAvailability',

  // Employee Account Panel Children
  VIEW_PERSONAL_DETAILS: 'viewPersonalDetails',
  VIEW_SHIFTS: 'viewShifts',
  VIEW_AVAILABILITY: 'viewAvailability',
};

export const DRAWER_CONFIG = {
  // Base Drawer Types
  [DRAWER_TYPES.EMPLOYEE]: {
    component: EmployeeAccountPanel,
    parent: null,
    props: (props) => ({
      employee: props.drawerData,
      handleCloseDrawer: props.handleCloseDrawer,
      handleOpenDrawer: props.handleOpenDrawer,
    }),
  },
  [DRAWER_TYPES.USER]: {
    component: UserAccountPanel,
    parent: null,
    props: (props) => ({
      employee: props.drawerData,
      handleCloseDrawer: props.handleCloseDrawer,
      handleOpenDrawer: props.handleOpenDrawer,
    }),
  },

  // Children of UserAccountPanel
  [DRAWER_TYPES.EDIT_PERSONAL_DETAILS]: {
    component: EditPersonalDetails,
    parent: DRAWER_TYPES.USER,
    props: (props) => ({
      employeeId: props.drawerData.id,
      handleOpenDrawer: props.handleOpenDrawer,
    }),
  },

  [DRAWER_TYPES.AVAILABILITY]: {
    component: UserAvailabilityPanel,
    parent: DRAWER_TYPES.USER,
    props: (props) => ({
      user: props.drawerData,
      handleOpenDrawer: props.handleOpenDrawer,
      drawerTypes: DRAWER_TYPES,
    }),
  },

  // Children of Availability (Grandchildren of UserAccountPanel)
  [DRAWER_TYPES.EDIT_AVAILABILITY]: {
    component: EditEvent,
    parent: DRAWER_TYPES.AVAILABILITY,
    props: (props) => ({
      user: props.drawerData,
      isNewEvent: false,
      handleBackNavigation: props.handleBackNavigation,
    }),
  },
  [DRAWER_TYPES.CREATE_AVAILABILITY]: {
    component: EditEvent,
    parent: DRAWER_TYPES.AVAILABILITY,
    props: (props) => ({
      user: props.drawerData,
      isNewEvent: true,
      handleBackNavigation: props.handleBackNavigation,
    }),
  },

  [DRAWER_TYPES.EDIT_NOTIFICATIONS]: {
    component: UserNotificationSettings,
    parent: DRAWER_TYPES.USER,
    props: (props) => ({
      employee: props.drawerData,
      handleOpenDrawer: props.handleOpenDrawer,
    }),
  },

  // Employee Drawer Types
  [DRAWER_TYPES.VIEW_PERSONAL_DETAILS]: {
    component: EmployeePersonalDetailsView,
    parent: DRAWER_TYPES.EMPLOYEE,
    props: (props) => ({
      employee: props.drawerData,
    }),
  },
  [DRAWER_TYPES.VIEW_SHIFTS]: {
    component: EmployeeShiftView,
    parent: DRAWER_TYPES.EMPLOYEE,
    props: (props) => ({
      employee: props.drawerData,
    }),
  },
  [DRAWER_TYPES.VIEW_AVAILABILITY]: {
    component: EmployeeAvailabilityView,
    parent: DRAWER_TYPES.EMPLOYEE,
    props: (props) => ({
      user: props.drawerData,
      handleOpenDrawer: props.handleOpenDrawer,
      drawerTypes: DRAWER_TYPES,
    }),
  },

  // Tips Drawers
  [DRAWER_TYPES.RECORD_PAYMENT]: {
    component: RecordPaymentDrawer,
    parent: null,
    props: (props) => ({
      employee: props.drawerData.employee,
      onConfirm: props.drawerData.onConfirm,
      handleCloseDrawer: props.handleCloseDrawer,
    }),
  },
  [DRAWER_TYPES.TIPS_HISTORY]: {
    component: TipsHistoryDrawer,
    parent: null,
    props: (props) => ({
      employee: props.drawerData,
      handleCloseDrawer: props.handleCloseDrawer,
    }),
  },
};
